import { createRoute, Link, useNavigate } from '@tanstack/react-router';
import { Route as rootRoute } from '../__root';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { collection, getDocs, query, where, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useCartStore, useAuthStore } from '../../store';
import { MapPin, Store, Package, MessageCircle, FileText, Mail, Search, ChevronRight, Home } from 'lucide-react';
import { toast } from 'sonner';

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/shop/$vendorId',
  component: VendorProfile,
});

interface Vendor {
  uid: string;
  storeName: string;
  storeDescription: string;
  city: string;
  bannerImage?: string;
  bannerColors?: string[];
  bannerType?: 'image' | 'color' | 'gradient';
  gradientType?: string;
  profilePicture?: string;
  themeColors?: string[];
  themeType?: 'solid' | 'gradient';
  policies?: string;
  contactEmail?: string;
  socialLinks?: {
    instagram?: string;
    facebook?: string;
    twitter?: string;
    website?: string;
  };
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  vendorId: string;
  isArchived?: boolean;
  stock?: number;
  maxOrderQuantity?: number;
  category?: string;
  dimensions?: string;
  details?: string;
}

function VendorProfile() {
  const { vendorId } = Route.useParams();
  const navigate = useNavigate();
  const { profile } = useAuthStore();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const addItem = useCartStore((state) => state.addItem);

  useEffect(() => {
    const fetchVendorData = async () => {
      try {
        const vendorDoc = await getDoc(doc(db, 'stores', vendorId));
        if (vendorDoc.exists()) {
          setVendor({ uid: vendorDoc.id, ...vendorDoc.data() } as Vendor);
        }

        const q = query(collection(db, 'products'), where('vendorId', '==', vendorId));
        const snapshot = await getDocs(q);
        const productsData = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Product[];
        setProducts(productsData.filter(p => !p.isArchived));
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchVendorData();
  }, [vendorId]);

  const handleMessageVendor = async () => {
    if (!auth.currentUser) {
      toast.error('Please log in to message this store');
      return;
    }

    try {
      const q = query(
        collection(db, 'chat_threads'), 
        where('customerId', '==', auth.currentUser.uid), 
        where('vendorId', '==', vendorId)
      );
      const snap = await getDocs(q);
      
      if (snap.empty) {
        await addDoc(collection(db, 'chat_threads'), {
           customerId: auth.currentUser.uid,
           customerName: profile?.displayName || auth.currentUser.email || 'Customer',
           vendorId: vendor?.uid || vendorId,
           vendorName: vendor?.storeName || 'Vendor',
           lastMessage: '',
           lastMessageAt: serverTimestamp(),
           readStatus: {
             [auth.currentUser.uid]: true,
             [vendor?.uid || vendorId]: true
           }
        });
      }
      navigate({ to: '/profile', search: { tab: 'messages' } });
    } catch (err) {
      console.error(err);
      toast.error('Could not start conversation');
    }
  };

  if (loading) {
    return (
      <div className="flex-1 flex justify-center items-center h-[60vh] bg-neutral-50 dark:bg-neutral-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neutral-900 dark:border-white"></div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="flex-1 flex flex-col justify-center items-center h-[60vh] text-center bg-neutral-50 dark:bg-neutral-900">
        <Store className="w-16 h-16 text-neutral-300 dark:text-neutral-600 mb-4" />
        <h2 className="text-2xl font-serif text-neutral-900 dark:text-white">Store Not Found</h2>
        <p className="text-neutral-500 dark:text-neutral-400 mt-2">This vendor doesn't exist or has been removed.</p>
        <Link to="/" className="mt-6 text-sm font-medium text-neutral-900 dark:text-white hover:underline">Return to Market</Link>
      </div>
    );
  }

  const getBannerStyle = () => {
    if (vendor.bannerType === 'image' && vendor.bannerImage) {
      return { backgroundImage: `url(${vendor.bannerImage})`, backgroundSize: 'cover', backgroundPosition: 'center' };
    }
    if (vendor.bannerType === 'gradient' && vendor.bannerColors && vendor.bannerColors.length > 0) {
      const colors = vendor.bannerColors.join(', ');
      switch (vendor.gradientType) {
        case 'linear-tb': return { backgroundImage: `linear-gradient(to bottom, ${colors})` };
        case 'linear-lr': return { backgroundImage: `linear-gradient(to right, ${colors})` };
        case 'linear-diag': return { backgroundImage: `linear-gradient(135deg, ${colors})` };
        case 'radial': return { backgroundImage: `radial-gradient(circle at center, ${colors})` };
        case 'mesh': return { backgroundImage: `linear-gradient(45deg, ${colors})` };
        default: return { backgroundColor: vendor.bannerColors[0] };
      }
    }
    if (vendor.bannerType === 'color' && vendor.bannerColors && vendor.bannerColors.length > 0) {
      return { backgroundColor: vendor.bannerColors[0] };
    }
    return { backgroundColor: '#f3f4f6' };
  };

  const getThemeStyle = () => {
    if (vendor.themeType === 'gradient' && vendor.themeColors && vendor.themeColors.length > 0) {
      return { backgroundImage: `linear-gradient(135deg, ${vendor.themeColors.join(', ')})` };
    }
    // Respect system dark mode variables if no custom color is set
    return vendor.themeColors?.[0] ? { backgroundColor: vendor.themeColors[0] } : {};
  };

  const getContrastTextClass = (hexColor: string | undefined) => {
    if (!hexColor) return 'text-neutral-900 dark:text-white';
    const hex = hexColor.replace('#', '');
    if (hex.length !== 6 && hex.length !== 3) return 'text-neutral-900 dark:text-white';
    
    let r, g, b;
    if (hex.length === 3) {
      r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
      g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
      b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    } else {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? 'text-neutral-900' : 'text-white';
  };

  const getBannerContrastClass = () => {
    if (vendor.bannerType === 'image') return 'text-white'; // Default to white for images, could be improved with image analysis
    
    const hexColor = vendor.bannerColors?.[0];
    if (!hexColor) return 'text-neutral-900';
    
    const hex = hexColor.replace('#', '');
    if (hex.length !== 6 && hex.length !== 3) return 'text-neutral-900';
    
    let r, g, b;
    if (hex.length === 3) {
      r = parseInt(hex.charAt(0) + hex.charAt(0), 16);
      g = parseInt(hex.charAt(1) + hex.charAt(1), 16);
      b = parseInt(hex.charAt(2) + hex.charAt(2), 16);
    } else {
      r = parseInt(hex.substring(0, 2), 16);
      g = parseInt(hex.substring(2, 4), 16);
      b = parseInt(hex.substring(4, 6), 16);
    }
    
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? 'text-neutral-900' : 'text-white';
  };

  const contrastClass = getContrastTextClass(vendor.themeColors?.[0]);
  const bannerContrastClass = getBannerContrastClass();

  const primaryCategory = products.length > 0
    ? (products.reduce((acc, p) => {
        const cat = p.category;
        if (cat) acc[cat] = (acc[cat] || 0) + 1;
        return acc;
      }, {} as Record<string, number>) as Record<string, number>)
    : {};

  const topCategory = Object.entries(primaryCategory).sort((a, b) => b[1] - a[1])[0]?.[0] || 'Handmade';

  const vendorTitle = `Shop ${topCategory} by ${vendor.storeName} | Cardston Market`;
  const vendorDescription = vendor.storeDescription
    ? `Shop ${topCategory.toLowerCase()} by ${vendor.storeName} on Cardston Market. ${vendor.storeDescription}`
    : `Shop ${topCategory.toLowerCase()} by ${vendor.storeName} on Cardston Market. Discover unique local artisan goods from Cardston, Alberta.`;

  return (
    <div className="min-h-screen" style={getThemeStyle()}>
      <Helmet>
        <title>{vendorTitle}</title>
        <meta name="description" content={vendorDescription} />
        <link rel="canonical" href={`https://www.cardstonmarket.com/shop/${vendorId}`} />
        <meta property="og:title" content={vendorTitle} />
        <meta property="og:description" content={vendorDescription} />
        {vendor.profilePicture && <meta property="og:image" content={vendor.profilePicture} />}
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={`https://www.cardstonmarket.com/shop/${vendorId}`} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={vendorTitle} />
        <meta name="twitter:description" content={vendorDescription} />
        {vendor.profilePicture && <meta name="twitter:image" content={vendor.profilePicture} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": vendor.storeName,
          "description": vendor.storeDescription || `Visit ${vendor.storeName} on Cardston Market`,
          "url": `https://www.cardstonmarket.com/shop/${vendorId}`,
          "image": vendor.profilePicture || vendor.bannerImage,
          "address": vendor.city ? { "@type": "PostalAddress", "addressLocality": vendor.city } : undefined,
          ...(vendor.contactEmail ? { "email": vendor.contactEmail } : {}),
          ...(vendor.socialLinks?.website ? { "sameAs": [vendor.socialLinks.website] } : {}),
          ...(vendor.socialLinks?.instagram ? { "sameAs": [...(vendor.socialLinks.website ? [vendor.socialLinks.website] : []), `https://instagram.com/${vendor.socialLinks.instagram.startsWith('http') ? '' : ''}${vendor.socialLinks.instagram}`] } : {})
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cardstonmarket.com/" },
            { "@type": "ListItem", "position": 2, "name": vendor.storeName, "item": `https://www.cardstonmarket.com/shop/${vendorId}` }
          ]
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": `${vendor.storeName} - Local Vendor on Cardston Market`,
          "description": vendor.storeDescription || `Shop local goods from ${vendor.storeName} on Cardston Market.`,
          "url": `https://www.cardstonmarket.com/shop/${vendorId}`,
          "isPartOf": { "@id": "https://www.cardstonmarket.com/#website" },
          "speakable": {
            "@type": "SpeakableSpecification",
            "cssSelector": ["h1", "p"]
          }
        })}</script>
      </Helmet>
      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <ol className="flex items-center gap-1.5 text-sm text-neutral-500 dark:text-neutral-400">
          <li><Link to="/" className="hover:text-neutral-900 dark:hover:text-white transition-colors flex items-center gap-1"><Home className="w-3.5 h-3.5" /> Home</Link></li>
          <li><ChevronRight className="w-3.5 h-3.5 shrink-0" /></li>
          <li><span className="text-neutral-900 dark:text-white font-medium">{vendor.storeName}</span></li>
        </ol>
      </nav>

      {/* Vendor Header / Banner */}
      <div 
        className="w-full transition-all duration-700 relative z-10 shadow-sm mb-12"
        style={getBannerStyle()}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="px-4 py-12 sm:px-8 sm:py-16 flex flex-col sm:flex-row items-center sm:items-end gap-6 sm:gap-8 text-center sm:text-left">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full border-4 border-white shadow-xl bg-neutral-100 flex items-center justify-center shrink-0 overflow-hidden relative z-20">
              {vendor.profilePicture ? (
                <img src={vendor.profilePicture} alt={`${vendor.storeName} vendor profile on Cardston Market`} className="w-full h-full object-cover" fetchPriority="high" />
              ) : (
                <Store className="w-12 h-12 text-neutral-400" />
              )}
            </div>
            <div className="flex-1 pt-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <h1 className={`text-4xl font-serif font-medium mb-2 tracking-tight ${bannerContrastClass}`}>{vendor.storeName || 'Unnamed Store'}</h1>
                  <div className={`flex items-center justify-center sm:justify-start gap-2 mb-4 opacity-80 ${bannerContrastClass}`}>
                    <MapPin className="w-4 h-4" />
                    <span className="text-sm font-medium">{vendor.city || 'Local Area'}</span>
                  </div>
                </div>
                
                <div className="flex justify-center sm:justify-end">
                  <button 
                    onClick={handleMessageVendor}
                    className={`flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-medium backdrop-blur-md transition-colors shadow-sm
                      ${getContrastTextClass(vendor.themeColors?.[0]) === 'text-white' 
                        ? 'bg-black/20 hover:bg-black/30 text-white border border-white/20' 
                        : 'bg-white/50 hover:bg-white/70 text-neutral-900 border border-neutral-900/10'}`}
                  >
                    <MessageCircle className="w-4 h-4" />
                    Message Store
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          <div className={`px-4 pb-12 sm:px-8 border-t border-white/20 pt-8 ${bannerContrastClass}`}>
            <p className="max-w-3xl text-lg font-light leading-relaxed opacity-90">
              {vendor.storeDescription || 'Welcome to my local shop!'}
            </p>
          </div>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 relative z-10">

        {/* Vendor Policies and Info */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-1 space-y-6">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-800 shadow-sm">
              <h3 className="font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
                 <Store className="w-4 h-4 text-neutral-400" /> About Store
              </h3>
              {vendor.contactEmail && (
                <div className="mb-4 text-sm">
                  <span className="block text-neutral-500 dark:text-neutral-400 mb-1">Contact</span>
                  <a href={`https://mail.google.com/mail/?view=cm&fs=1&to=${vendor.contactEmail}`} target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-white hover:underline flex items-center gap-2">
                    <Mail className="w-4 h-4 text-neutral-400" /> {vendor.contactEmail}
                  </a>
                </div>
              )}
              {vendor.socialLinks && Object.values(vendor.socialLinks).some(link => link) && (
                 <div className="mb-4 text-sm">
                  <span className="block text-neutral-500 dark:text-neutral-400 mb-2">Socials & Links</span>
                  <div className="flex flex-col gap-2">
                     {vendor.socialLinks.instagram && <a href={vendor.socialLinks.instagram.startsWith('http') ? vendor.socialLinks.instagram : `https://instagram.com/${vendor.socialLinks.instagram}`} target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-white hover:underline">Instagram</a>}
                     {vendor.socialLinks.facebook && <a href={vendor.socialLinks.facebook.startsWith('http') ? vendor.socialLinks.facebook : `https://facebook.com/${vendor.socialLinks.facebook}`} target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-white hover:underline">Facebook</a>}
                     {vendor.socialLinks.twitter && <a href={vendor.socialLinks.twitter.startsWith('http') ? vendor.socialLinks.twitter : `https://twitter.com/${vendor.socialLinks.twitter}`} target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-white hover:underline">Twitter / X</a>}
                     {vendor.socialLinks.website && <a href={vendor.socialLinks.website.startsWith('http') ? vendor.socialLinks.website : `https://${vendor.socialLinks.website}`} target="_blank" rel="noopener noreferrer" className="text-neutral-900 dark:text-white hover:underline">Website</a>}
                  </div>
                </div>
              )}
            </div>

            {vendor.policies && (
              <div className="bg-white dark:bg-neutral-900 rounded-2xl p-6 border border-neutral-200 dark:border-neutral-800 shadow-sm">
                <h3 className="font-medium text-neutral-900 dark:text-white mb-4 flex items-center gap-2 text-sm uppercase tracking-wider">
                   <FileText className="w-4 h-4 text-neutral-400" /> Store Policies
                </h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 whitespace-pre-wrap leading-relaxed">
                  {vendor.policies}
                </p>
              </div>
            )}
          </div>
          
          <div className="md:col-span-3">
            {/* Products Grid Header & Search */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-8 gap-4">
              <h2 className={`text-2xl font-serif font-medium flex items-center gap-2 ${contrastClass}`}>
                <Package className={`w-5 h-5 ${contrastClass} opacity-70`} />
                Products from {vendor.storeName}
              </h2>
              
              <div className="relative w-full sm:w-64 shrink-0">
                <input
                  type="text"
                  placeholder="Search store..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 bg-white/10 border border-neutral-900/10 dark:border-white/10 rounded-full text-sm placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-neutral-900/20 dark:focus:ring-white/20 backdrop-blur-md transition-all shadow-sm"
                  style={{ color: contrastClass === 'text-white' ? 'white' : 'black' }}
                />
                <Search className={`w-4 h-4 absolute left-3 top-2.5 opacity-50 ${contrastClass}`} />
              </div>
            </div>

            {products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description.toLowerCase().includes(searchQuery.toLowerCase())).length === 0 ? (
              <div className="text-center py-24 bg-neutral-50 dark:bg-neutral-800/80 rounded-2xl border border-neutral-100 dark:border-neutral-800">
                <Search className="w-12 h-12 text-neutral-300 dark:text-neutral-600 mx-auto mb-4 stroke-[1.5]" />
                <h3 className="text-lg font-medium text-neutral-900 dark:text-white mb-2">No products found</h3>
                <p className="text-neutral-500 dark:text-neutral-400">
                  {searchQuery ? `No items match "${searchQuery}".` : "This store hasn't added any products yet."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.filter(p => p.name.toLowerCase().includes(searchQuery.toLowerCase()) || p.description.toLowerCase().includes(searchQuery.toLowerCase())).map((product) => (
            <div key={product.id} className="group flex flex-col bg-white dark:bg-neutral-900 rounded-2xl p-4 shadow-sm border border-neutral-100 dark:border-neutral-800 transition-all hover:shadow-md">
              <div className="relative aspect-[4/5] rounded-xl overflow-hidden bg-neutral-100 dark:bg-neutral-800 mb-4">
                <Link to="/product/$productId" params={{ productId: product.id }} className="absolute inset-0 z-0">
                  <img
                    src={product.imageUrl || 'https://picsum.photos/seed/product/400/500'}
                    alt={`Handmade ${product.name} by ${vendor.storeName} on Cardston Market`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 dark:group-hover:bg-white/5 transition-colors duration-300" />
                </Link>
              </div>
              
              <div className="flex flex-col flex-1">
                <div className="flex justify-between items-start mb-2 gap-4">
                  <Link to="/product/$productId" params={{ productId: product.id }} className="text-lg font-medium text-neutral-900 dark:text-white line-clamp-2 hover:underline">{product.name}</Link>
                  <span className="text-lg font-medium text-neutral-900 dark:text-white shrink-0">${(product.price / 100).toFixed(2)}</span>
                </div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400 line-clamp-2 mb-2 font-light flex-1">{product.description}</p>
                {product.stock !== undefined && (
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
                    {product.stock > 0 ? `${product.stock} in stock` : <span className="text-red-500 dark:text-red-400 font-medium">Out of stock</span>}
                  </p>
                )}
                
                <button
                  onClick={() => {
                    addItem({
                      id: product.id,
                      name: product.name,
                      price: product.price,
                      quantity: 1,
                      imageUrl: product.imageUrl,
                      description: product.description,
                      vendorId: vendor.uid,
                      vendorName: vendor.storeName,
                      stock: product.stock,
                      maxOrderQuantity: product.maxOrderQuantity
                    });
                    toast.success(`${product.name} added to cart`);
                  }}
                  disabled={product.stock === 0}
                  style={getThemeStyle()}
                  className={`w-full py-3.5 px-4 rounded-xl text-sm font-medium transition-all duration-300 ${
                    product.stock === 0 
                      ? 'opacity-50 cursor-not-allowed grayscale' 
                      : `hover:opacity-90 ${contrastClass}`
                  }`}
                >
                  {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
          </div>
        </div>
      </div>
    </div>
  );
}
